import { useParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import SeoHead from "@/components/feature/SeoHead";
import { movies, categories } from "@/mocks/movies";

function getCategoryName(slug: string): string {
  const cat = categories.find((c) => c.slug === slug);
  return cat ? cat.name : slug;
}

export default function Category() {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const [activeCat, setActiveCat] = useState(categorySlug || "all");
  const [sortBy, setSortBy] = useState<"rating" | "year">("rating");

  useEffect(() => {
    if (categorySlug) {
      setActiveCat(categorySlug);
    }
  }, [categorySlug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeCat]);

  const catName = activeCat === "all" ? "全部影片" : getCategoryName(activeCat);

  let filteredMovies =
    activeCat === "all"
      ? [...movies]
      : movies.filter((m) => m.category === activeCat);

  filteredMovies = [...filteredMovies].sort((a, b) =>
    sortBy === "rating" ? b.rating - a.rating : b.year - a.year
  );

  const seoTitle = `${catName} - 春夜影院免费高清在线观看`;
  const seoDescription = `春夜影院提供${catName}免费在线观看，蓝光画质流畅播放不缓冲。海量${catName}资源每日更新，手机版便捷访问，随时随地畅享高清观影体验。`;
  const seoKeywords = `春夜影院,${catName},在线观看,免费高清,蓝光画质,手机版`;

  return (
    <>
      <SeoHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl={`https://www.chunyemovie.com/category/${activeCat}`}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: catName },
              ]}
            />
          </div>

          {/* Header */}
          <div className="bg-background-950 border-b border-background-800">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
              <h1 className="text-3xl font-bold text-foreground-50" style={{ fontFamily: "var(--font-heading)" }}>
                {catName}
              </h1>
              <p className="text-foreground-400 text-sm mt-2">
                共 {filteredMovies.length} 部影片 · 蓝光高清画质 · 流畅播放不卡顿
              </p>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="border-b border-background-200/70 bg-background-50">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="flex items-center gap-1 overflow-x-auto py-3 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveCat(cat.slug)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      activeCat === cat.slug
                        ? "bg-primary-500 text-foreground-50"
                        : "bg-background-100 text-foreground-600 hover:text-foreground-900 hover:bg-background-200"
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Sort */}
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4 flex items-center gap-3">
            <span className="text-foreground-500 text-sm">排序：</span>
            <button
              onClick={() => setSortBy("rating")}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                sortBy === "rating"
                  ? "bg-secondary-500 text-foreground-50"
                  : "bg-background-100 text-foreground-600 hover:text-foreground-900"
              }`}
            >
              <i className="ri-star-line mr-1"></i>评分最高
            </button>
            <button
              onClick={() => setSortBy("year")}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                sortBy === "year"
                  ? "bg-secondary-500 text-foreground-50"
                  : "bg-background-100 text-foreground-600 hover:text-foreground-900"
              }`}
            >
              <i className="ri-calendar-line mr-1"></i>最新上映
            </button>
          </div>

          {/* Movie Grid */}
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 pb-12">
            {filteredMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {filteredMovies.map((movie) => (
                  <MovieCard
                    key={movie.id}
                    id={movie.id}
                    title={movie.title}
                    slug={movie.slug}
                    year={movie.year}
                    rating={movie.rating}
                    categoryName={getCategoryName(movie.category)}
                    posterUrl={movie.posterUrl}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <i className="ri-film-line text-6xl text-foreground-400 mb-4 block"></i>
                <p className="text-foreground-500 text-lg">暂无相关影片</p>
                <p className="text-foreground-600 text-sm mt-2">该分类下暂无影片，请查看其他分类</p>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}